import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutGrid, LogOut, Mail, Menu, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { groupsFor, hasAny, menuFor } from "../../lib/perm";
import { LangSwitch } from "../common/LangSwitch";
import { Logo } from "../common/Logo";
import { EmailPrefs } from "../common/EmailPrefs";
import { NotificationBell } from "../common/NotificationBell";
import { NotifySettings } from "../common/NotifySettings";
import { PushPrompt } from "../common/PushPrompt";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { StaffNav, isActiveItem } from "./StaffNav";
import { NavCtx } from "../../apps/staff/StaffMenu";

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1320].forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.18);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.16);
      o.start(ctx.currentTime + i * 0.18); o.stop(ctx.currentTime + i * 0.18 + 0.17);
    });
  } catch (e) { /* audio blocked until the user interacts */ }
};

const usePoll = (enabled, url, ms, event, onData) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const poll = () => api.get(url).then((r) => onData(r.data)).catch(() => {});
    poll();
    const id = setInterval(poll, ms);
    if (event) window.addEventListener(event, poll);
    return () => { clearInterval(id); if (event) window.removeEventListener(event, poll); };
  }, [enabled, url, ms, event, onData]);
};

const useBadges = (user, t) => {
  const [b, setB] = useState({ orders: 0, chat: 0, leave: 0, offers: 0, faults: 0 });
  const prev = useRef(null);
  const onOrders = useCallback((d) => {
    if (prev.current !== null && d.waiting > prev.current) { beep(); toast.info(t("ops.newOrders", { n: d.waiting })); }
    prev.current = d.waiting;
    setB((s) => ({ ...s, orders: d.waiting }));
  }, [t]);
  const onChat = useCallback((d) => setB((s) => ({ ...s, chat: d.unread })), []);
  const onMenu = useCallback((d) => setB((s) => ({ ...s, ...d })), []);
  usePoll(hasAny(user, ["orders:review", "orders:queue"]), "/staff/orders/summary", 20000, "vavlis:queue", onOrders);
  usePoll(hasAny(user, ["customers:read"]), "/staff/chat/unread", 30000, "vavlis:chat", onChat);
  usePoll(true, "/staff/menu-badges", 60000, "vavlis:badges", onMenu);
  return b;
};

const useNavPrefs = () => {
  const [prefs, setPrefs] = useState({ pinned: [], open: null, usage: {} });
  const timer = useRef(null);
  useEffect(() => { api.get("/me/nav-prefs").then((r) => setPrefs(r.data)).catch(() => {}); }, []);
  const update = useCallback((patch) => setPrefs((s) => {
    const next = { ...s, ...patch };
    clearTimeout(timer.current);
    timer.current = setTimeout(() => api.put("/me/nav-prefs", next).catch(() => {}), 600);
    return next;
  }), []);
  return [prefs, update];
};

const BottomItem = ({ m, badge }) => {
  const { t } = useI18n();
  const active = isActiveItem(m, useLocation());
  const Icon = m.icon;
  return (
    <Link to={m.to} data-testid={`staff-bottom-${m.key}`} className={`relative flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-0.5 text-[10px] font-medium ${active ? "text-navy" : "text-slate-500"}`}>
      <Icon className={`h-5 w-5 ${active ? "text-brand" : ""}`} /><span className="max-w-full truncate">{t(`snav.short.${m.key}`)}</span>
      {badge > 0 && <span data-testid={`staff-bottom-badge-${m.key}`} className="absolute right-[18%] top-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1 text-[11px] font-bold text-navy">{badge}</span>}
    </Link>
  );
};

export const AppShell = ({ children }) => {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [more, setMore] = useState(false);
  const [dlg, setDlg] = useState(null);
  const groups = groupsFor(user);
  const flat = menuFor(user);
  const b = useBadges(user, t);
  const [prefs, setPrefs] = useNavPrefs();
  const badgeOf = (m) => (m.badge ? b[m.badge] : 0);
  const onGo = (m) => { setMore(false); setPrefs({ usage: { ...prefs.usage, [m.key]: (prefs.usage[m.key] || 0) + 1 } }); };
  const onAction = (a) => { setMore(false); if (a === "logout") logout(); else setDlg(a); };
  const links = flat.filter((m) => m.to);
  const primary = [...links].sort((x, y) => (prefs.usage[y.key] || 0) - (prefs.usage[x.key] || 0) || links.indexOf(x) - links.indexOf(y)).slice(0, 4);
  const nav = (prefix) => <StaffNav groups={groups} badgeOf={badgeOf} prefs={prefs} onPrefs={setPrefs} onAction={onAction} onGo={onGo} prefix={prefix} />;
  const total = flat.reduce((s, m) => s + (badgeOf(m) || 0), 0);

  return (
    <NavCtx.Provider value={{ groups, badgeOf, prefs, setPrefs, onGo, onAction }}>
    <div className="min-h-screen bg-canvas">
      <header className="sticky top-0 z-20 flex h-16 items-center gap-2 bg-navy px-3 text-white lg:px-8" data-testid="staff-topbar">
        <Link to="/staff/menu" aria-label="Vavlis" className="shrink-0"><span className="sm:hidden"><Logo compact className="h-7" /></span><span className="hidden sm:block"><Logo className="h-7" /></span></Link>
        <Link to="/staff/menu" data-testid="staff-menu-button" className="relative ml-2 inline-flex h-10 items-center gap-2 rounded-full bg-white/10 px-4 text-sm font-semibold hover:bg-white/20">
          <LayoutGrid className="h-4 w-4" />{t("snav.menu")}{total > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1 text-[11px] font-bold text-navy" data-testid="staff-menu-badge">{total > 99 ? "99+" : total}</span>}
        </Link>
        <span className="ml-2 hidden truncate text-xs text-white/60 lg:block" data-testid="current-user-name">{user.name}</span>
        <div className="ml-auto flex items-center gap-1">
          <LangSwitch dark />
          <NotificationBell />
          <button type="button" data-testid="email-prefs-button" onClick={() => setDlg("mail")} aria-label={t("x.mail.prefsTitle")} className="hidden h-11 w-11 place-items-center rounded-full hover:bg-white/10 sm:grid"><Mail className="h-5 w-5" /></button>
          <button type="button" data-testid="logout-button" onClick={logout} aria-label={t("common.logout")} className="grid h-11 w-11 place-items-center rounded-full hover:bg-white/10"><LogOut className="h-5 w-5" /></button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 pb-28 pt-6 lg:px-8 lg:pb-12">{children}</main>
      <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-slate-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden" data-testid="staff-bottom-nav">
        {primary.map((m) => <BottomItem key={m.key} m={m} badge={badgeOf(m)} />)}
        <Link to="/staff/menu" data-testid="staff-bottom-more" className="relative flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-slate-500">
          <LayoutGrid className="h-5 w-5" />{t("snav.menu")}
          {flat.some((m) => !primary.includes(m) && badgeOf(m) > 0) && <span className="absolute right-[30%] top-2 h-2.5 w-2.5 rounded-full bg-brand" data-testid="staff-bottom-more-dot" />}
        </Link>
      </nav>
      {more && (
        <div className="fixed inset-0 z-40 bg-navy/40 lg:hidden" onClick={() => setMore(false)} data-testid="staff-more-sheet">
          <div className="absolute inset-x-0 bottom-0 flex max-h-[85vh] flex-col rounded-t-3xl bg-navy text-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center px-4 pt-3"><span className="text-sm font-semibold">{t("snav.more")}</span>
              <button type="button" onClick={() => setMore(false)} aria-label="close" data-testid="staff-more-close" className="ml-auto grid h-11 w-11 place-items-center"><X className="h-5 w-5" /></button></div>
            <div className="overflow-y-auto px-4 pb-8">{nav("staff-more")}</div>
          </div>
        </div>
      )}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto p-0" data-testid="email-prefs-dialog">
          <DialogTitle className="sr-only">{t("x.mail.prefsTitle")}</DialogTitle>
          {dlg === "notify" && <div className="p-4" data-testid="notify-settings-dialog"><NotifySettings onTest={() => {}} /></div>}
          <EmailPrefs cats={hasAny(user, ["orders:review", "orders:queue"]) ? ["orders", "hr"] : ["hr"]} />
        </DialogContent>
      </Dialog>
      <PushPrompt staff />
    </div>
    </NavCtx.Provider>
  );
};
